"use client";

import { useState, useEffect } from "react";
import {
  X,
  Wrench,
  Shield,
  Database,
  Copy,
  Check,
  ClipboardList,
} from "lucide-react";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { useInsuranceConnector } from "@frontend/components/InsuranceConnector/insurance-connector-provider";
import type { StorefrontDevToolTab } from "./types";

function CopyButton({ getText }: { getText: () => string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="px-3 py-1.5 bg-gray-800 text-white text-xs rounded-md hover:bg-gray-700 transition-colors flex items-center gap-1.5"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          Copy
        </>
      )}
    </button>
  );
}

function InsuranceMachinePanel() {
  let insuranceState: ReturnType<typeof useInsuranceConnector>["state"] | null =
    null;

  try {
    const connector = useInsuranceConnector();
    insuranceState = connector.state;
  } catch {
    // Provider not available
  }

  if (!insuranceState) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        Insurance connector machine not active
      </div>
    );
  }

  const json = JSON.stringify(
    { value: insuranceState.value, context: insuranceState.context },
    null,
    2,
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <span className="text-sm font-medium text-gray-700">
          State + Context
        </span>
        <CopyButton getText={() => json} />
      </div>
      <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-gray-800 bg-gray-50">
        {json}
      </pre>
    </div>
  );
}

function GlobalStorePanel() {
  const storeState = useBoundStore.getState();
  const json = JSON.stringify(storeState, null, 2);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <span className="text-sm font-medium text-gray-700">
          Full Store State
        </span>
        <CopyButton getText={() => json} />
      </div>
      <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-gray-800 bg-gray-50">
        {json}
      </pre>
    </div>
  );
}

function CopyAllForLLMButton() {
  const [copied, setCopied] = useState(false);

  let insuranceState: ReturnType<typeof useInsuranceConnector>["state"] | null =
    null;
  try {
    const connector = useInsuranceConnector();
    insuranceState = connector.state;
  } catch {
    // Provider not available
  }

  const handleCopy = async () => {
    const insuranceJson = insuranceState
      ? JSON.stringify({
          value: insuranceState.value,
          context: insuranceState.context,
        })
      : "null";
    const storeJson = JSON.stringify(useBoundStore.getState());

    const text = `Insurance Machine:\n${insuranceJson}\n\nGlobal Store:\n${storeJson}`;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="px-3 py-1.5 bg-blue-700 text-white text-xs rounded-md hover:bg-blue-600 transition-colors flex items-center gap-1.5"
      aria-label="Copy insurance machine and global store for LLM"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          Copied
        </>
      ) : (
        <>
          <ClipboardList className="h-3.5 w-3.5" />
          Copy All for LLM
        </>
      )}
    </button>
  );
}

const TABS: { id: StorefrontDevToolTab; label: string; icon: typeof Shield }[] =
  [
    { id: "insurance", label: "Insurance Machine", icon: Shield },
    { id: "store", label: "Global Store", icon: Database },
  ];

export function StorefrontDevTool() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<StorefrontDevToolTab>("insurance");

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "D") {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 p-4 bg-gray-900 hover:bg-gray-800 text-white rounded-full shadow-lg transition-all duration-200 hover:scale-110"
        title="Open Storefront DevTools (Ctrl+Shift+D)"
        aria-label="Open Storefront DevTools"
      >
        <Wrench className="h-5 w-5" />
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setIsOpen(false);
          }}
        >
          <dialog
            open
            className="relative bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            aria-modal="true"
          >
            {/* Header with tabs */}
            <div className="flex items-center justify-between border-b border-gray-200">
              <div className="flex">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-5 py-3.5 text-sm font-medium transition-colors flex items-center gap-2 border-b-2 ${
                      activeTab === tab.id
                        ? "border-blue-600 text-blue-700 bg-blue-50/50"
                        : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mr-2">
                <CopyAllForLLMButton />
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
                  aria-label="Close DevTools"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {activeTab === "insurance" && <InsuranceMachinePanel />}
              {activeTab === "store" && <GlobalStorePanel />}
            </div>
          </dialog>
        </div>
      )}
    </>
  );
}
